<script setup>
// NOOVI: Lixeira por pipeline (retenção de 30 dias). Lista os cards descartados
// do pipeline aberto; admin pode restaurar (volta ao board) ou excluir
// definitivamente (com confirmação destrutiva). Estados: loading, vazio, erro
// com retry, lista paginada, restore/exclusão por item.
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Modal from '../../../../components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { usePipelineTrash } from '../composables/usePipelineTrash';

import { REPORTING_DEFAULT_TIMEZONE } from 'dashboard/helper/reportingTimezone';
const props = defineProps({
  pipelineId: { type: [Number, String], default: null },
});

const show = defineModel('show', { type: Boolean, default: false });
const { t, locale } = useI18n();
const store = useStore();

// Locale-aware (achado Codex): o date-fns ptBR fixo punha "às" mesmo em UI
// inglês. Intl segue a locale ativa (mesmo padrão do AgendaTab). pt_BR → pt-BR.
const userLocale = () =>
  (
    locale.value ||
    (typeof navigator === 'undefined' ? '' : navigator.language) ||
    'en-US'
  ).replace('_', '-');

const reportingTimezone = computed(() => {
  const account = store.getters.getCurrentAccount || {};
  return (
    account.reporting_timezone ||
    account.settings?.reporting_timezone ||
    REPORTING_DEFAULT_TIMEZONE
  );
});

const {
  loading,
  error,
  items,
  meta,
  isEmpty,
  isRestoring,
  isDeleting,
  isBusy,
  fetchTrash,
  restore,
  permanentlyDelete,
  reset,
} = usePipelineTrash();

const cardPendingDelete = ref(null);
const showDeleteConfirmation = ref(false);

const currentPage = computed(() => meta.value.current_page || 1);
const totalPages = computed(() => meta.value.total_pages || 0);
const hasPrev = computed(() => currentPage.value > 1);
const hasNext = computed(() => currentPage.value < totalPages.value);
const hasBusyItems = computed(() => items.value.some(card => isBusy(card.id)));
const isDeleteConfirmationBusy = computed(
  () =>
    Boolean(cardPendingDelete.value) && isDeleting(cardPendingDelete.value.id)
);

const load = (page = 1) => fetchTrash({ pipelineId: props.pipelineId, page });

// Abre/recarrega ao abrir o modal; limpa o estado ao fechar.
watch(
  show,
  isVisible => {
    if (isVisible) {
      load(1);
    } else {
      reset();
      cardPendingDelete.value = null;
      showDeleteConfirmation.value = false;
    }
  },
  { immediate: true }
);

const goToPage = page => {
  if (page < 1 || page > totalPages.value) return;
  load(page);
};

const formatDiscardedAt = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(userLocale(), {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: reportingTimezone.value,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(userLocale(), {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: 'UTC',
    }).format(date);
  }
};

const stageName = card => card.current_stage?.name || card.pipeline_stage || '';

const requestDelete = card => {
  if (isBusy(card.id)) return;
  cardPendingDelete.value = card;
  showDeleteConfirmation.value = true;
};

const confirmDelete = async () => {
  const card = cardPendingDelete.value;
  if (!card || isDeleteConfirmationBusy.value) return;

  await permanentlyDelete(card);
  if (!items.value.some(item => item.id === card.id)) {
    showDeleteConfirmation.value = false;
    cardPendingDelete.value = null;
  }
};

const closeDeleteConfirmation = () => {
  if (isDeleteConfirmationBusy.value) return;
  showDeleteConfirmation.value = false;
  cardPendingDelete.value = null;
};

const onClose = () => {
  if (hasBusyItems.value) return;
  show.value = false;
};
</script>

<template>
  <Modal
    :show="show"
    size="large"
    :close-on-backdrop-click="!hasBusyItems"
    :close-on-escape="!hasBusyItems"
    :show-close-button="!hasBusyItems"
    @close="onClose"
  >
    <div class="flex flex-col max-h-[80vh] overflow-hidden">
      <!-- Header -->
      <div class="flex items-start gap-3 px-4 pb-4 pt-5 sm:px-6 sm:pt-6">
        <div
          class="flex size-12 flex-shrink-0 items-center justify-center rounded-lg bg-n-alpha-2"
          aria-hidden="true"
        >
          <Icon icon="i-lucide-trash-2" class="size-5 text-n-slate-11" />
        </div>
        <div class="min-w-0">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('PIPELINE.TRASH.TITLE') }}
          </h3>
          <p class="mt-0.5 text-sm text-n-slate-11">
            {{ t('PIPELINE.TRASH.SUBTITLE') }}
          </p>
        </div>
      </div>

      <!-- Body -->
      <div class="min-h-[200px] flex-1 overflow-y-auto px-4 pb-2 sm:px-6">
        <!-- Loading -->
        <div
          v-if="loading"
          class="flex flex-col items-center justify-center gap-3 py-12 text-n-slate-10"
          role="status"
          aria-live="polite"
        >
          <Spinner class="!w-6 !h-6" />
          <span class="text-sm">{{ t('PIPELINE.TRASH.LOADING') }}</span>
        </div>

        <!-- Error -->
        <div
          v-else-if="error"
          class="flex flex-col items-center justify-center gap-3 py-12 text-center"
          role="alert"
        >
          <Icon icon="i-lucide-alert-triangle" class="size-8 text-n-ruby-9" />
          <p class="text-sm text-n-slate-11">
            {{ t('PIPELINE.TRASH.ERROR.MESSAGE') }}
          </p>
          <Button
            variant="faded"
            color="slate"
            size="lg"
            icon="i-lucide-rotate-ccw"
            :label="t('PIPELINE.TRASH.ERROR.RETRY')"
            @click="load(currentPage)"
          />
        </div>

        <!-- Empty -->
        <div
          v-else-if="isEmpty"
          class="flex flex-col items-center justify-center gap-3 py-12 text-center"
        >
          <div
            class="flex items-center justify-center size-12 rounded-full bg-n-alpha-2"
          >
            <Icon icon="i-lucide-trash-2" class="size-6 text-n-slate-9" />
          </div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('PIPELINE.TRASH.EMPTY.TITLE') }}
          </p>
          <p class="text-xs text-n-slate-10 max-w-xs">
            {{ t('PIPELINE.TRASH.EMPTY.DESCRIPTION') }}
          </p>
        </div>

        <!-- List -->
        <ul v-else class="flex flex-col gap-2">
          <li
            v-for="card in items"
            :key="card.id"
            class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 sm:flex-row sm:items-start"
          >
            <div class="flex-1 min-w-0 flex flex-col gap-1.5">
              <!-- Title + stage -->
              <div class="flex items-center gap-2 flex-wrap">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ card.title || t('PIPELINE.TRASH.CARD.UNTITLED') }}
                </span>
                <span
                  v-if="stageName(card)"
                  class="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11"
                >
                  {{ stageName(card) }}
                </span>
              </div>

              <!-- Contact + owner -->
              <div
                v-if="card.contact?.name || card.owner?.name"
                class="flex items-center gap-3 text-xs text-n-slate-11 flex-wrap"
              >
                <span v-if="card.contact?.name" class="flex items-center gap-1">
                  <Icon
                    icon="i-lucide-user"
                    class="size-3 text-n-slate-9"
                    aria-hidden="true"
                  />
                  {{ card.contact.name }}
                </span>
                <span v-if="card.owner?.name" class="flex items-center gap-1">
                  <Icon
                    icon="i-lucide-user-check"
                    class="size-3 text-n-slate-9"
                    aria-hidden="true"
                  />
                  {{ card.owner.name }}
                </span>
              </div>

              <!-- Discarded meta -->
              <div
                class="flex items-center gap-3 text-xs text-n-slate-10 flex-wrap"
              >
                <span v-if="card.discarded_at" class="flex items-center gap-1">
                  <Icon
                    icon="i-lucide-clock"
                    class="size-3"
                    aria-hidden="true"
                  />
                  {{ formatDiscardedAt(card.discarded_at) }}
                </span>
                <span
                  v-if="card.discarded_by?.name"
                  class="flex items-center gap-1"
                >
                  <Icon
                    icon="i-lucide-user-x"
                    class="size-3"
                    aria-hidden="true"
                  />
                  {{
                    t('PIPELINE.TRASH.CARD.DISCARDED_BY', {
                      name: card.discarded_by.name,
                    })
                  }}
                </span>
              </div>

              <!-- Reason -->
              <p
                v-if="card.discard_reason"
                class="text-xs text-n-slate-11 italic"
              >
                {{
                  t('PIPELINE.TRASH.CARD.REASON', {
                    reason: card.discard_reason,
                  })
                }}
              </p>

              <!-- Days remaining -->
              <span
                class="inline-flex items-center gap-1 self-start text-[11px] font-medium px-1.5 py-0.5 rounded-md"
                :class="
                  card.days_until_permanent_deletion <= 3
                    ? 'bg-n-ruby-3 text-n-ruby-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                <Icon
                  v-if="card.days_until_permanent_deletion <= 3"
                  icon="i-lucide-alert-triangle"
                  class="size-3"
                  aria-hidden="true"
                />
                {{
                  card.days_until_permanent_deletion <= 0
                    ? t('PIPELINE.TRASH.CARD.DAYS_LAST')
                    : t('PIPELINE.TRASH.CARD.DAYS_REMAINING', {
                        count: card.days_until_permanent_deletion,
                      })
                }}
              </span>
            </div>

            <!-- Actions -->
            <div class="flex flex-shrink-0 items-center justify-end gap-1">
              <Button
                v-tooltip="{
                  content: t('PIPELINE.TRASH.RESTORE.LABEL'),
                  placement: 'top',
                }"
                variant="ghost"
                color="slate"
                size="lg"
                icon="i-lucide-rotate-ccw"
                :is-loading="isRestoring(card.id)"
                :disabled="isBusy(card.id)"
                :aria-label="t('PIPELINE.TRASH.RESTORE.LABEL')"
                @click="restore(card)"
              />
              <Button
                v-tooltip="{
                  content: t('PIPELINE.TRASH.DELETE.LABEL'),
                  placement: 'top',
                }"
                variant="ghost"
                color="ruby"
                size="lg"
                icon="i-lucide-trash-2"
                :is-loading="isDeleting(card.id)"
                :disabled="isBusy(card.id)"
                :aria-label="t('PIPELINE.TRASH.DELETE.LABEL')"
                @click="requestDelete(card)"
              />
            </div>
          </li>
        </ul>
      </div>

      <!-- Footer / pagination -->
      <div
        class="flex flex-col gap-3 border-t border-n-weak px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
      >
        <span class="text-xs text-n-slate-10">
          {{ t('PIPELINE.TRASH.COUNT', { count: meta.total_count || 0 }) }}
        </span>
        <div v-if="totalPages > 1" class="flex items-center gap-1">
          <Button
            variant="ghost"
            color="slate"
            size="lg"
            icon="i-lucide-chevron-left"
            :disabled="!hasPrev || loading"
            :aria-label="t('PIPELINE.TRASH.PAGINATION.PREVIOUS')"
            @click="goToPage(currentPage - 1)"
          />
          <span class="text-xs text-n-slate-11 tabular-nums px-1">
            {{
              t('PIPELINE.TRASH.PAGINATION.PAGE_OF', {
                current: currentPage,
                total: totalPages,
              })
            }}
          </span>
          <Button
            variant="ghost"
            color="slate"
            size="lg"
            icon="i-lucide-chevron-right"
            :disabled="!hasNext || loading"
            :aria-label="t('PIPELINE.TRASH.PAGINATION.NEXT')"
            @click="goToPage(currentPage + 1)"
          />
        </div>
      </div>
    </div>
  </Modal>

  <Modal
    :show="showDeleteConfirmation"
    :close-on-backdrop-click="!isDeleteConfirmationBusy"
    :close-on-escape="!isDeleteConfirmationBusy"
    :show-close-button="!isDeleteConfirmationBusy"
    initial-focus-selector="#pipeline-trash-delete-confirm"
    @close="closeDeleteConfirmation"
  >
    <section
      class="p-4 sm:p-6"
      :aria-busy="isDeleteConfirmationBusy"
      data-modal="pipeline-trash-delete-confirmation"
    >
      <h2 class="pr-10 text-base font-semibold text-n-slate-12">
        {{ t('PIPELINE.TRASH.DELETE.CONFIRM_TITLE') }}
      </h2>
      <p class="mt-3 break-words text-sm text-n-slate-11">
        {{
          t('PIPELINE.TRASH.DELETE.CONFIRM_DESCRIPTION', {
            title:
              cardPendingDelete?.title || t('PIPELINE.TRASH.CARD.UNTITLED'),
          })
        }}
      </p>
      <footer
        class="mt-6 flex flex-col-reverse gap-2 border-t border-n-weak pt-4 sm:flex-row"
      >
        <Button
          variant="faded"
          color="slate"
          size="lg"
          :label="t('PIPELINE.TRASH.DELETE.CANCEL')"
          :disabled="isDeleteConfirmationBusy"
          class="w-full flex-1"
          @click="closeDeleteConfirmation"
        />
        <Button
          id="pipeline-trash-delete-confirm"
          color="ruby"
          size="lg"
          :label="t('PIPELINE.TRASH.DELETE.CONFIRM_BUTTON')"
          :is-loading="isDeleteConfirmationBusy"
          :disabled="isDeleteConfirmationBusy"
          class="w-full flex-1"
          @click="confirmDelete"
        />
      </footer>
    </section>
  </Modal>
</template>
